<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { supportedLanguages } from '@/router'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const openLang = ref(false)
const underConstruction = ref(false)
import BrandLogo from '@/components/BrandLogo.vue'

import {
  ChevronDownIcon,
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  BeakerIcon,
  BuildingOffice2Icon,
  BuildingLibraryIcon,
  UserGroupIcon,
  CpuChipIcon,
  BriefcaseIcon
} from '@heroicons/vue/24/outline'


// --- 1. LOGIQUE DE COULEUR DE NAVBAR CORRIGÉE ---
// Liste des "slugs" de pages claires (sans la langue)
const lightPageSlugs = ['solutions', '/contact', '/privacy', '/terms']

const shouldShowDarkNav = computed(() => {
  // Si on scrolle, toujours foncé
  if (scrollY.value > 50) return true
  
  // Sinon, on vérifie si l'URL actuelle *contient* un des slugs de page claire
  // Ex: '/fr/why-bifrost' contient '/why-bifrost' -> TRUE
  return lightPageSlugs.some(slug => route.path.includes(slug))
})


// --- 2. HELPER POUR LES LIENS (Garde la langue actuelle) ---
const localPath = (path) => {
  // Si le path est déjà complet ou externe, on ne touche pas
  if (path.startsWith('http')) return path
  // Sinon on injecte la locale : /platform -> /fr/platform
  return `/${locale.value}${path}`
}

// --- Locale HTML attributes ---
const setHtmlLangDir = (val) => {
  try {
    const l = val || 'fr'
    document.documentElement.lang = l === 'ar' ? 'ar' : l
    document.documentElement.dir = l === 'ar' ? 'rtl' : 'ltr'
  } catch (e) { /* ignore */ }
}

// Identité de page, préfixe de langue inclus : changer de page OU de
// langue déclenche le même fondu doux (le scrollBehavior remonte en haut
// pendant que la page est invisible — pas de traduction "en place" sèche).
const pageKey = computed(() => route.path)

const setLocale = (code) => {
  openLang.value = false
  if (code === locale.value) return
  // On ne touche PAS à la locale ici : elle bascule dans onPageAfterLeave,
  // une fois l'ancienne page fondue au noir. Sinon les textes se
  // retraduiraient "en direct" sous les yeux du lecteur avant le fondu.
  const newPath = route.fullPath.replace(/^\/(fr|en|ar)(?=\/|$)/, `/${code}`)
  router.push(newPath.startsWith(`/${code}`) ? newPath : `/${code}`)
}

// Fin du fondu de sortie : l'écran est vide, on peut basculer la langue
// sans que ce soit visible (couvre aussi le back/forward entre langues).
const onPageAfterLeave = () => {
  const lang = route.params.lang
  if (lang && supportedLanguages.includes(lang) && lang !== locale.value) {
    locale.value = lang
  }
}

// --- State Management ---
const mobileMenuOpen = ref(false)
const scrollY = ref(0)
const activeDropdown = ref(null)
const activeSubItem = ref(null)

// computed pour que le menu suive le changement de langue
const menuData = computed(() => ({
  solutions: {
    title: t('navmenu.subitems.solutions.title'),
    items: [
      {
        id: 'pharma',
        title: t('navmenu.subitems.solutions.items.pharma.title'),
        desc: t('navmenu.subitems.solutions.items.pharma.desc'),
        icon: BeakerIcon
      },
      {
        id: 'corporate',
        title: t('navmenu.subitems.solutions.items.corporate.title'),
        desc: t('navmenu.subitems.solutions.items.corporate.desc'),
        icon: BuildingOffice2Icon
      },
      {
        id: 'institutions',
        title: t('navmenu.subitems.solutions.items.institutions.title'),
        desc: t('navmenu.subitems.solutions.items.institutions.desc'),
        icon: BuildingLibraryIcon
      }
    ],
    defaultDesc: t('navmenu.subitems.solutions.defaultDesc')
  },
  partners: {
    title: t('navmenu.subitems.partners.title'),
    items: [
      {
        id: 'agencies',
        title: t('navmenu.subitems.partners.items.agencies.title'),
        desc: t('navmenu.subitems.partners.items.agencies.desc'),
        icon: UserGroupIcon
      },
      {
        id: 'technology',
        title: t('navmenu.subitems.partners.items.technology.title'),
        desc: t('navmenu.subitems.partners.items.technology.desc'),
        icon: CpuChipIcon
      },
      {
        id: 'pro',
        // Produit externe de la maison (bifrost-events.pro), pas une page interne
        external: 'https://www.bifrost-events.pro',
        title: t('navmenu.subitems.partners.items.pro.title'),
        desc: t('navmenu.subitems.partners.items.pro.desc'),
        icon: BriefcaseIcon
      }
    ],
    defaultDesc: t('navmenu.subitems.partners.defaultDesc')
  }
}))

const setMenuCategory = (category) => {
  activeDropdown.value = category
  if (category && menuData.value[category]) {
    activeSubItem.value = menuData.value[category].items[0]
  }
}

const handleScroll = () => {
  scrollY.value = window.scrollY
}

// SPA : pas de rechargement au changement de page, et la souris n'ayant pas
// bougé, le @mouseleave des mega-menus ne se déclenche pas — on referme donc
// tout ce qui est ouvert dès que la route change (clic, back/forward...).
watch(() => route.fullPath, () => {
  activeDropdown.value = null
  activeSubItem.value = null
  mobileMenuOpen.value = false
  openLang.value = false
})

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  underConstruction.value = import.meta.env.VITE_UNDER_CONSTRUCTION || 'false'

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-reveal')
        observer.unobserve(entry.target)
      }
    })
  }, { threshold: 0.1 })

  document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
    observer.observe(el)
  })

  setHtmlLangDir(locale.value)
  watch(locale, (v) => setHtmlLangDir(v))

  // Close dropdown on outside click
  const handleClickOutside = (event) => {
    const dropdown = document.querySelector('.lang-dropdown')
    if (dropdown && !dropdown.contains(event.target)) {
      openLang.value = false
    }
  }
  document.addEventListener('click', handleClickOutside)
  onUnmounted(() => document.removeEventListener('click', handleClickOutside))
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <!-- overflow-x-clip (not hidden): clips horizontal bleed without creating
       a scroll container, which would break every position:sticky descendant -->
  <div class="text-slate-800 antialiased overflow-x-clip selection:bg-secondary-500 selection:text-white bg-slate-50" v-if="underConstruction !== 'true'">

    <!-- ================= NAVIGATION ================= -->
    <nav class="fixed w-full z-50 transition-all duration-300"
      :class="shouldShowDarkNav ? 'bg-white/90 backdrop-blur-xl border-b border-slate-200 py-3' : 'bg-transparent py-5 border-transparent'">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center">

          <!-- Logo (Link corrigé) -->
          <router-link :to="`/${locale}`" class="flex-shrink-0 flex items-center cursor-pointer z-50">
            <BrandLogo
              :size="50"
              :variant="shouldShowDarkNav ? 'gradient' : 'white'"
              class="transition-colors duration-200"
              :class="shouldShowDarkNav ? 'text-slate-950' : 'text-white'"
              role="img"
              aria-label="Bifrost"
            />
          </router-link>

          <!-- Desktop Menu -->
          <div class="hidden md:flex space-x-8 items-center">

            <!-- 1. PLATFORM (Link corrigé avec localPath) -->
            <router-link :to="localPath('/platform')" class="font-medium transition text-sm"
              :class="shouldShowDarkNav ? 'text-slate-700 hover:text-primary-700' : 'text-slate-200 hover:text-white'">
              {{ $t('navmenu.platform', 'Platform') }}
            </router-link>

            <!-- 2. WHY BIFROST (Link corrigé) -->
            <router-link :to="localPath('/why-bifrost')" class="font-medium transition text-sm"
              :class="shouldShowDarkNav ? 'text-slate-700 hover:text-primary-700' : 'text-slate-200 hover:text-white'">
              {{ $t('navmenu.why', 'Why Bifrost ?') }}
            </router-link>

            <!-- 2b. PHILOSOPHY -->
            <router-link :to="localPath('/philosophy')" class="font-medium transition text-sm"
              :class="shouldShowDarkNav ? 'text-slate-700 hover:text-primary-700' : 'text-slate-200 hover:text-white'">
              {{ $t('navmenu.philosophy', 'Philosophy') }}
            </router-link>

            <!-- 3. SOLUTIONS -->
            <div class="relative group" @mouseenter="setMenuCategory('solutions')" @mouseleave="setMenuCategory(null)">
              <button class="flex items-center font-medium transition duration-200 text-sm"
                :class="shouldShowDarkNav ? 'text-slate-700 hover:text-primary-700' : 'text-slate-200 hover:text-white'">
                {{ $t('navmenu.solutions', 'Solutions') }}
                <ChevronDownIcon class="w-4 h-4 ml-1 opacity-70" />
              </button>

              <transition name="fade-up">
                <div v-if="activeDropdown === 'solutions'" class="absolute top-full -left-20 pt-4">
                  <div class="w-[640px] bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden flex">
                    <div class="w-1/2 p-3">
                      <p class="px-3 pt-2 pb-2 text-[11px] font-mono uppercase tracking-widest text-slate-400">
                        {{ menuData.solutions.title }}
                      </p>
                      <router-link v-for="item in menuData.solutions.items" :key="item.id" :to="localPath(`/solutions/${item.id}`)" @mouseenter="activeSubItem = item" @click="setMenuCategory(null)"
                        class="flex items-center p-3 rounded-md cursor-pointer transition-colors duration-150"
                        :class="activeSubItem?.id === item.id ? 'bg-slate-50 text-slate-900' : 'text-slate-600 hover:bg-slate-50'">
                        <component :is="item.icon" class="w-5 h-5 me-3 flex-shrink-0"
                          :class="activeSubItem?.id === item.id ? 'text-primary-600' : 'text-slate-400'" />
                        <span class="font-semibold text-sm">{{ item.title }}</span>
                      </router-link>
                    </div>
                    <div class="w-1/2 bg-slate-50 border-s border-slate-200 p-6 flex flex-col justify-center">
                      <h4 class="font-semibold text-slate-900 mb-2">{{ activeSubItem?.title }}</h4>
                      <p class="text-sm text-slate-500 leading-relaxed">{{ activeSubItem?.desc }}</p>
                      <router-link :to="localPath(`/solutions/${activeSubItem?.id}`)" @click="setMenuCategory(null)"
                        class="mt-5 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-primary-600 hover:text-primary-700 transition group/link">
                        {{ $t('nav.learnMore', 'Learn more') }}
                        <ArrowRightIcon class="w-3 h-3 transition-transform group-hover/link:translate-x-0.5 rtl:rotate-180" />
                      </router-link>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- 4. PARTNERS -->
            <div class="relative group" @mouseenter="setMenuCategory('partners')" @mouseleave="setMenuCategory(null)">
              <button class="flex items-center font-medium transition duration-200 text-sm"
                :class="shouldShowDarkNav ? 'text-slate-700 hover:text-primary-700' : 'text-slate-200 hover:text-white'">
                {{ $t('navmenu.partners', 'Partners') }}
                <ChevronDownIcon class="w-4 h-4 ml-1 opacity-70" />
              </button>

              <transition name="fade-up">
                <div v-if="activeDropdown === 'partners'" class="absolute top-full -left-20 pt-4">
                  <div class="w-[640px] bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden flex">
                    <div class="w-1/2 p-3">
                      <p class="px-3 pt-2 pb-2 text-[11px] font-mono uppercase tracking-widest text-slate-400">
                        {{ menuData.partners.title }}
                      </p>
                      <!-- item.external : produit hors site (Bifrost Pro) → <a>, sinon router-link -->
                      <component v-for="item in menuData.partners.items" :key="item.id"
                        :is="item.external ? 'a' : 'RouterLink'"
                        v-bind="item.external ? { href: item.external, target: '_blank', rel: 'noopener' } : { to: localPath(`/partners/${item.id}`) }"
                        @mouseenter="activeSubItem = item" @click="setMenuCategory(null)"
                        class="flex items-center p-3 rounded-md cursor-pointer transition-colors duration-150"
                        :class="activeSubItem?.id === item.id ? 'bg-slate-50 text-slate-900' : 'text-slate-600 hover:bg-slate-50'">
                        <component :is="item.icon" class="w-5 h-5 me-3 flex-shrink-0"
                          :class="activeSubItem?.id === item.id ? 'text-primary-600' : 'text-slate-400'" />
                        <span class="font-semibold text-sm">{{ item.title }}</span>
                        <ArrowTopRightOnSquareIcon v-if="item.external" class="w-3.5 h-3.5 ms-auto text-slate-400" />
                      </component>
                    </div>
                    <div class="w-1/2 bg-slate-50 border-s border-slate-200 p-6 flex flex-col justify-center">
                      <h4 class="font-semibold text-slate-900 mb-2">{{ activeSubItem?.title }}</h4>
                      <p class="text-sm text-slate-500 leading-relaxed">{{ activeSubItem?.desc }}</p>
                      <component :is="activeSubItem?.external ? 'a' : 'RouterLink'"
                        v-bind="activeSubItem?.external ? { href: activeSubItem.external, target: '_blank', rel: 'noopener' } : { to: localPath(`/partners/${activeSubItem?.id}`) }"
                        @click="setMenuCategory(null)"
                        class="mt-5 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-primary-600 hover:text-primary-700 transition group/link">
                        {{ $t('nav.learnMore', 'Learn more') }}
                        <ArrowRightIcon class="w-3 h-3 transition-transform group-hover/link:translate-x-0.5 rtl:rotate-180" />
                      </component>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- 5. CASE STUDIES (Link corrigé) -->
            <!-- <router-link :to="localPath('/case-studies')" class="font-medium transition text-sm"
              :class="shouldShowDarkNav ? 'text-slate-700 hover:text-primary-700' : 'text-slate-200 hover:text-white'">
              Case Studies
            </router-link> -->

          </div>

          <!-- Buttons -->
          <div class="hidden md:flex items-center space-x-4">
            <!-- <a href="#" class="font-medium transition text-sm"
              :class="shouldShowDarkNav ? 'text-slate-700 hover:text-primary-700' : 'text-slate-200 hover:text-white'">{{
                $t('nav.login', 'Login') }}</a> -->
            <a href="https://bifrost.events/touch" target="_blank"
              class="px-6 py-2.5 font-semibold rounded-md transition text-sm"
              :class="shouldShowDarkNav ? 'bg-slate-950 text-white hover:bg-slate-800' : 'bg-white text-slate-950 hover:bg-slate-200'">
              {{ $t('navmenu.book', 'Book Demo') }}
            </a>
          </div>

          <!-- Mobile Toggle -->
          <div class="md:hidden flex items-center">
            <button @click="mobileMenuOpen = !mobileMenuOpen" :class="shouldShowDarkNav ? 'text-slate-800' : 'text-white'">
              <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16">
                </path>
              </svg>
            </button>
          </div>
        </div>
      </div>

       <!-- Mobile Menu Overlay -->
       <transition name="fade-up">
        <div v-if="mobileMenuOpen" class="absolute top-full left-0 w-full bg-white border-b border-slate-200 shadow-xl p-4 flex flex-col space-y-4 md:hidden">
            <router-link :to="localPath('/platform')" class="text-slate-700 font-bold" @click="mobileMenuOpen = false">{{ $t('navmenu.platform', 'Platform') }}</router-link>
            <router-link :to="localPath('/why-bifrost')" class="text-slate-700 font-bold" @click="mobileMenuOpen = false">{{ $t('navmenu.why', 'Why Bifrost ?') }}</router-link>
            <router-link :to="localPath('/philosophy')" class="text-slate-700 font-bold" @click="mobileMenuOpen = false">{{ $t('navmenu.philosophy', 'Philosophy') }}</router-link>
            
            <div class="border-t border-slate-100 pt-2">
                <p class="text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-2">{{ $t('navmenu.solutions', 'Solutions') }}</p>
                <router-link v-for="item in menuData.solutions.items" :key="item.id" :to="localPath(`/solutions/${item.id}`)" class="block py-2 text-slate-600 text-sm flex items-center" @click="mobileMenuOpen = false">
                  <component :is="item.icon" class="w-4 h-4 mr-2" /> {{ item.title }}
                </router-link>
            </div>
            
            <div class="border-t border-slate-100 pt-2">
                <p class="text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-2">{{ $t('navmenu.partners', 'Partners') }}</p>
                <component v-for="item in menuData.partners.items" :key="item.id"
                  :is="item.external ? 'a' : 'RouterLink'"
                  v-bind="item.external ? { href: item.external, target: '_blank', rel: 'noopener' } : { to: localPath(`/partners/${item.id}`) }"
                  class="block py-2 text-slate-600 text-sm flex items-center" @click="mobileMenuOpen = false">
                   <component :is="item.icon" class="w-4 h-4 mr-2" /> {{ item.title }}
                   <ArrowTopRightOnSquareIcon v-if="item.external" class="w-3.5 h-3.5 ms-1.5 text-slate-400" />
                </component>
            </div>

            <!-- <router-link :to="localPath('/case-studies')" class="text-slate-700 font-bold" @click="mobileMenuOpen = false">Case Studies</router-link> -->
            <a href="https://bifrost.events/touch" class="bg-slate-950 text-white text-center py-3 rounded-md font-semibold" @click="mobileMenuOpen = false">{{ $t('navmenu.book', 'Book Demo') }}</a>
        </div>
      </transition>
    </nav>

    <!-- Fondu entre pages (les pages sont multi-racines → wrapper keyé) -->
    <router-view v-slot="{ Component }">
      <transition name="page" mode="out-in" @after-leave="onPageAfterLeave">
        <div v-if="Component" :key="pageKey">
          <component :is="Component" />
        </div>
      </transition>
    </router-view>

    <!-- FOOTER -->
    <footer class="bg-slate-50 pt-20 pb-10 border-t border-slate-200">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10 mb-16">
          <div class="col-span-2 lg:col-span-2">
            <BrandLogo :size="50" class="text-slate-950 mb-4" role="img" aria-label="Bifrost" />
            <p class="text-slate-500 text-sm leading-relaxed max-w-xs">
              {{ $t('footer.platformDesc', 'The all-in-one event orchestration platform. Designed for scalability, security, and human connection.') }}
            </p>
          </div>
          <div>
            <h4 class="font-semibold text-slate-900 mb-6">{{ $t('footer.cols.platform') }}</h4>
            <ul class="space-y-4 text-sm text-slate-600">
              <li><router-link :to="localPath('/platform')" class="hover:text-primary-700 transition">{{ $t('navmenu.platform') }}</router-link></li>
              <li><router-link :to="localPath('/why-bifrost')" class="hover:text-primary-700 transition">{{ $t('navmenu.why') }}</router-link></li>
            </ul>
          </div>
          <div>
            <h4 class="font-semibold text-slate-900 mb-6">{{ $t('footer.cols.solutions') }}</h4>
            <ul class="space-y-4 text-sm text-slate-600">
              <li><router-link :to="localPath('/solutions/pharma')" class="hover:text-primary-700 transition">{{ $t('navmenu.subitems.solutions.items.pharma.title') }}</router-link></li>
              <li><router-link :to="localPath('/solutions/corporate')" class="hover:text-primary-700 transition">{{ $t('navmenu.subitems.solutions.items.corporate.title') }}</router-link></li>
              <li><router-link :to="localPath('/solutions/institutions')" class="hover:text-primary-700 transition">{{ $t('navmenu.subitems.solutions.items.institutions.title') }}</router-link></li>
            </ul>
          </div>
          <div>
            <h4 class="font-semibold text-slate-900 mb-6">{{ $t('footer.cols.partners') }}</h4>
            <ul class="space-y-4 text-sm text-slate-600">
              <li><router-link :to="localPath('/partners/agencies')" class="hover:text-primary-700 transition">{{ $t('navmenu.subitems.partners.items.agencies.title') }}</router-link></li>
              <li><router-link :to="localPath('/partners/technology')" class="hover:text-primary-700 transition">{{ $t('navmenu.subitems.partners.items.technology.title') }}</router-link></li>
              <li>
                <a href="https://www.bifrost-events.pro" target="_blank" rel="noopener"
                  class="hover:text-primary-700 transition inline-flex items-center gap-1">
                  {{ $t('navmenu.subitems.partners.items.pro.title') }}
                  <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div
          class="pt-8 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center text-sm text-slate-400">
          <div>&copy; {{ new Date().getFullYear() }} Bifrost Events. {{ $t('footer.rights') }}</div>
          <div class="flex space-x-6 mt-4 md:mt-0 items-center">
            <router-link :to="localPath('/privacy')" class="hover:text-primary-700">{{ $t('footer.privacy') }}</router-link>
            <router-link :to="localPath('/terms')" class="hover:text-primary-700">{{ $t('footer.terms') }}</router-link>
            
            <!-- Language Switcher -->
            <div class="relative">
              <button @click="openLang = !openLang"
                class="flex items-center hover:text-primary-700 transition space-x-1 rtl:space-x-reverse">
                <span>{{ $t(`lang.${$i18n.locale}`) }}</span>
                <svg class="w-4 h-4 transition-transform" :class="openLang ? 'rotate-180' : ''" fill="none"
                  stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <transition name="fade-up">
              <div v-if="openLang"
                class="absolute right-0 bottom-full mb-2 rtl:left-0 rtl:right-auto w-32 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-20">
                <button v-for="lang in [
                  { code: 'fr', label: 'Français' },
                  { code: 'en', label: 'English' },
                  { code: 'ar', label: 'العربية' }
                ]" :key="lang.code" @click="setLocale(lang.code)"
                  class="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition">
                  {{ lang.label }}
                </button>
              </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </footer>

  </div>
  <router-view v-else />
</template>

<style>
/* --- Fondu entre pages (voir router-view) ---
   Sortie brève, entrée longue et décélérée avec une légère dérive
   verticale : le changement se lit comme un glissement progressif,
   jamais comme un flash. */
.page-enter-active {
  transition:
    opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}
.page-leave-active {
  transition: opacity 0.28s ease;
}
.page-enter-from {
  opacity: 0;
  transform: translateY(14px);
}
.page-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .page-enter-active,
  .page-leave-active {
    transition: none;
  }
  .page-enter-from {
    transform: none;
  }
}

/* Tailwind v4 émet les propriétés natives translate/scale/rotate (et non
   transform) : il faut réinitialiser les quatre, sinon les éléments
   "reveal" gardent leur décalage initial à vie et ne glissent jamais. */
.animate-reveal {
  opacity: 1 !important;
  transform: none !important;
  translate: 0 0 !important;
  scale: 1 !important;
  rotate: 0deg !important;
}

.fade-up-enter-active,
.fade-up-leave-active {
  transition: all 0.3s ease;
}

.fade-up-enter-from,
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>